import { type ReactNode, type RefObject } from "react";
import { Flex, InfoBox, Label, Text } from "@jsq/ds/core";
import {
  FormCheckbox,
  FormDatePicker,
  FormPercentInput,
  FormRadioButton,
  FormRadioGroup,
  FormSelect,
  FormTextarea,
  FormTextInput,
  useFormContext,
} from "@jsq/ds/forms";
import { Sizes } from "@jsq/ds/styles";
import CountryFormField from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/AddressFields/CountryFormField";
import {
  type FieldType,
  type FormFieldConfig,
  type SectionActionHandle,
  type TaxFormValues,
} from "../../types";
import { ControllingPersonsSection } from "../ControllingPersonsSection";
import { GiinField } from "../GiinField";
import { LabelWithTooltip } from "../LabelWithTooltip";
import { TaxResidencySection } from "../TaxResidencySection";
import * as styles from "./styles_vanilla.css";

export type FieldRendererFn = (
  field: FormFieldConfig,
  resolvedLabel: string,
  actionRef?: RefObject<SectionActionHandle | null>,
) => ReactNode;

type RadioFieldProps = {
  field: FormFieldConfig;
  resolvedLabel: string;
};

const RadioField = ({ field, resolvedLabel }: RadioFieldProps) => {
  const { values } = useFormContext<TaxFormValues>();
  const radioOptions = field.radioOptions ?? [];

  return (
    <Flex flexDirection="column" gap={Sizes.SM}>
      <Label required={field.required}>{resolvedLabel}</Label>
      <FormRadioGroup name={field.name} data-testid={`field-${field.name}`}>
        {radioOptions.map((option) => (
          <Flex key={option.value} flexDirection="column" className={styles.radioOption}>
            <FormRadioButton value={option.value}>
              <Text>{option.label}</Text>
            </FormRadioButton>
            {option.nestedField && values[field.name] === option.value && (
              <Flex flexDirection="column" className={styles.radioNestedField}>
                <Label required={option.nestedField.required}>{option.nestedField.label}</Label>
                <FormTextInput
                  name={option.nestedField.name}
                  placeholder={option.nestedField.placeholder}
                  data-testid={`field-${option.nestedField.name}`}
                />
              </Flex>
            )}
          </Flex>
        ))}
      </FormRadioGroup>
    </Flex>
  );
};

const renderTextInput: FieldRendererFn = (field, resolvedLabel) => {
  return (
    <Flex flexDirection="column">
      {field.tooltip ? (
        <LabelWithTooltip
          fieldName={field.name}
          label={resolvedLabel}
          tooltip={field.tooltip}
          required={field.required}
        />
      ) : (
        resolvedLabel && <Label required={field.required}>{resolvedLabel}</Label>
      )}
      <FormTextInput
        name={field.name}
        placeholder={field.placeholder}
        disabled={field.disabled}
        data-testid={`field-${field.name}`}
        isFullWidth
      />
    </Flex>
  );
};

const renderGiinInput: FieldRendererFn = (field, resolvedLabel) => (
  <Flex flexDirection="column">
    <LabelWithTooltip fieldName={field.name} label={resolvedLabel} tooltip={field.tooltip} />
    <GiinField
      name={field.name}
      disabled={field.disabled}
      data-testid={`field-${field.name}`}
    />
  </Flex>
);

const renderSelect: FieldRendererFn = (field, resolvedLabel) => {
  return (
    <Flex flexDirection="column">
      <FormSelect
        name={field.name}
        label={
          field.tooltip ? (
            <LabelWithTooltip
              fieldName={field.name}
              label={resolvedLabel}
              tooltip={field.tooltip}
              required={field.required}
            />
          ) : field.plainLabel ? (
            resolvedLabel
          ) : (
            resolvedLabel && <Label required={field.required}>{resolvedLabel}</Label>
          )
        }
        required={field.required && field.plainLabel}
        placeholder={field.placeholder ?? "Select an option"}
        disabled={field.disabled}
        full
        data-testid={`field-${field.name}`}
      >
        <FormSelect.Section>
          {(field.options ?? []).map((option) => (
            <FormSelect.Option key={option.value} value={option.value}>
              {option.label}
            </FormSelect.Option>
          ))}
        </FormSelect.Section>
      </FormSelect>
    </Flex>
  );
};

const renderCountry: FieldRendererFn = (field) => <CountryFormField field={field} />;

const renderDate: FieldRendererFn = (field, resolvedLabel) => (
  <Flex flexDirection="column">
    <LabelWithTooltip
      fieldName={field.name}
      label={resolvedLabel}
      tooltip={field.tooltip}
      required={field.required}
    />
    <FormDatePicker
      name={field.name}
      aria-label={resolvedLabel}
      disabled={field.disabled}
      hasIcon
      isFull
      placeholderText={field.placeholder ?? "MM/DD/YYYY"}
      data-testid={`field-${field.name}`}
    />
  </Flex>
);

const renderCheckbox: FieldRendererFn = (field, resolvedLabel) => {
  return (
    <Flex flexDirection="row" alignItems="flex-start" gap={Sizes.XS}>
      <Flex flexDirection="column">
        <FormCheckbox
          name={field.name}
          label={resolvedLabel}
          disabled={field.disabled}
          data-testid={`field-${field.name}`}
        />
      </Flex>
      {field.required && <div className={styles.requiredIndicator}>*</div>}
      {field.tooltip && (
        <LabelWithTooltip label="" fieldName={field.name} tooltip={field.tooltip} />
      )}
    </Flex>
  );
};

const renderPercentage: FieldRendererFn = (field, resolvedLabel) => (
  <FormPercentInput
    name={field.name}
    label={resolvedLabel}
    placeholder={field.placeholder ?? "0"}
    disabled={field.disabled}
    isRequired={field.required}
    isFullWidth
    data-testid={`field-${field.name}`}
  />
);

const renderTextarea: FieldRendererFn = (field, resolvedLabel) => (
  <FormTextarea
    name={field.name}
    label={resolvedLabel && <Label required={field.required}>{resolvedLabel}</Label>}
    placeholder={field.placeholder}
    disabled={field.disabled}
    isFullWidth
    size="x-small"
    data-testid={`field-${field.name}`}
  />
);

const renderFieldLabel: FieldRendererFn = (field, resolvedLabel) => (
  <Label required={field.required}>{resolvedLabel}</Label>
);

const renderInfo: FieldRendererFn = (_field, resolvedLabel) => (
  <InfoBox variant="basic" title={resolvedLabel} className={styles.infoBox} />
);

const renderDescription: FieldRendererFn = (_field, resolvedLabel) => (
  <Text>{resolvedLabel}</Text>
);

const renderRadio: FieldRendererFn = (field, resolvedLabel) => (
  <RadioField field={field} resolvedLabel={resolvedLabel} />
);

const renderTaxResidency: FieldRendererFn = (field, _resolvedLabel, actionRef) => (
  <TaxResidencySection fieldName={field.name} required={field.required} onAddRef={actionRef} />
);

const renderControllingPersons: FieldRendererFn = (field, _resolvedLabel, actionRef) => (
  <ControllingPersonsSection field={field} onAddRef={actionRef} />
);

export const FIELD_RENDERERS: Partial<Record<FieldType, FieldRendererFn>> = {
  text: renderTextInput,
  number: renderTextInput,
  giin: renderGiinInput,
  select: renderSelect,
  country: renderCountry,
  date: renderDate,
  checkbox: renderCheckbox,
  radio: renderRadio,
  percentage: renderPercentage,
  textarea: renderTextarea,
  label: renderFieldLabel,
  info: renderInfo,
  description: renderDescription,
  taxResidency: renderTaxResidency,
  controllingPersons: renderControllingPersons,
};
